import {MathJax, MathJaxContext} from "better-react-mathjax";
import {Player} from "@lottiefiles/react-lottie-player";
import doomsdayFuel from "../../../assets/json/3.2-doomsday-fuel.json";
import {getRandomColor} from "../../../utils/GetRandomColor";
import LinkedText from "../../LinkedText";
import IconText from "../../IconText";
import {faGasPump} from "@fortawesome/free-solid-svg-icons";

export default function DoomsdayFuel() {
    const lottieStyle = {
        border: `5px solid ${getRandomColor()}`,
    };

    const mathJaxConfig = {
        loader: {load: ["[tex]/html"]},
        tex: {
            packages: {"[+]": ["html"]},
            inlineMath: [["$", "$"], ["\\(", "\\)"]],
            displayMath: [["$$", "$$"], ["\\[", "\\]"]]
        }
    }

    return (
        <div className={"blog-body"}>
            <h1><IconText text={"3.2. Doomsday Fuel"} icon={faGasPump}/></h1>

            <h2>Question</h2>
            <div className={"blog-quote"}>
                <p>
                    Making fuel for the LAMBCHOP's reactor core is a tricky process because of the exotic matter
                    involved.
                    It starts as raw ore, then during processing, begins randomly changing between forms, eventually
                    reaching a stable form. There may be multiple stable forms that a sample could ultimately reach,
                    not all
                    of which are useful as fuel.
                </p>
                <p>
                    Commander Lambda has tasked you to help the scientists increase fuel creation efficiency by
                    predicting
                    the end state of a given ore sample. You have carefully studied the different structures that
                    the ore
                    can take and which transitions it undergoes. It appears that, while random, the probability of
                    each
                    structure transforming is fixed. That is, each time the ore is in 1 state, it has the same
                    probabilities
                    of entering the next state (which might be the same state). You have recorded the observed
                    transitions
                    in a matrix. The others in the lab have hypothesized more exotic forms that the ore can become,
                    but you
                    haven't seen all of them.
                </p>
                <p>
                    Write a function solution(m) that takes an array of array of nonnegative ints representing how
                    many
                    times that state has gone to the next state and return an array of ints for each terminal state
                    giving
                    the exact probabilities of each terminal state, represented as the numerator for each state,
                    then the
                    denominator for all of them at the end and in simplest form. The matrix is at most 10 by 10. It
                    is
                    guaranteed that no matter which state the ore is in, there is a path from that state to a
                    terminal
                    state. That is, the processing will always eventually end in a stable state. The ore starts in
                    state 0.
                    The denominator will fit within a signed 32-bit integer during the calculation, as long as the
                    fraction
                    is simplified regularly.
                </p>
                <p>
                    For example, consider the matrix m: <br/>
                    [ <br/>
                    [0,1,0,0,0,1], # s0, the initial state, goes to s1 and s5 with equal probability <br/>
                    [4,0,0,3,2,0], # s1 can become s0, s3, or s4, but with different probabilities <br/>
                    [0,0,0,0,0,0], # s2 is terminal, and unreachable (never observed in practice) <br/>
                    [0,0,0,0,0,0], # s3 is terminal <br/>
                    [0,0,0,0,0,0], # s4 is terminal <br/>
                    [0,0,0,0,0,0], # s5 is terminal <br/>
                    ] <br/>
                    So, we can consider different paths to terminal states, such as: <br/>
                    s0 -> s1 -> s3 <br/>
                    s0 -> s1 -> s0 -> s1 -> s0 -> s1 -> s4 <br/>
                    s0 -> s1 -> s0 -> s5 <br/>
                    Tracing the probabilities of each, we find that <br/>
                    s2 has probability 0 <br/>
                    s3 has probability 3/14 <br/>
                    s4 has probability 1/7 <br/>
                    s5 has probability 9/14 <br/>
                    So, putting that together, and making a common denominator, gives an answer in the form of <br/>
                    [s2.numerator, s3.numerator, s4.numerator, s5.numerator, denominator] which is <br/>
                    [0, 3, 2, 9, 14]. <br/>
                </p>
                <p>
                    <b>Test cases</b>
                </p>
                <p>
                    Input: <br/>
                    solution.solution([[0, 2, 1, 0, 0], [0, 0, 0, 3, 4], [0, 0, 0, 0, 0], [0, 0, 0, 0,0], [0, 0, 0,
                    0, 0]])
                    <br/>
                    Output: <br/>
                    [7, 6, 8, 21] <br/>
                    <br/>
                    Input: <br/>
                    solution.solution([[0, 1, 0, 0, 0, 1], [4, 0, 0, 3, 2, 0], [0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0,
                    0], [0,
                    0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0]]) <br/>
                    Output: <br/>
                    [0, 3, 2, 9, 14] <br/>
                </p>
            </div>

            <h2>Solution</h2>
            <p>
                This question reminds me of my stochastic differential equation lectures from my university, which
                makes it
                my favorite question in this challenge. Aside from fancy stuff, the problem can be reduced to an
                absorbing
                Markov chain. Watch this <LinkedText href={"https://www.youtube.com/embed/bTeKu7WdbT8?si=mI-jLSEXgAPyfcdu"} text={"video series"}></LinkedText> to learn how to solve the equation.
            </p>

            <p>
                Let's look at the absorbing Markov chain in our example. The given matrix is:
            </p>

            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`\\begin{bmatrix}
                    0 & 1 & 0 & 0 & 0 & 1 \\\\
                    4 & 0 & 0 & 3 & 2 & 0 \\\\
                    0 & 0 & 0 & 0 & 0 & 0 \\\\
                    0 & 0 & 0 & 0 & 0 & 0 \\\\
                    0 & 0 & 0 & 0 & 0 & 0 \\\\
                    0 & 0 & 0 & 0 & 0 & 0 \\\\
                    \\end{bmatrix}`}
                </MathJax>
            </MathJaxContext>

            <p>
                We can get the probabilities by dividing each number by the sum of the rows. Visualization can make
                it
                easier to grasp.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={doomsdayFuel} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>

            <p>
                We need to convert this matrix to a transition matrix with probabilities. In the code,
                the <code>create_transition_matrix</code> function handles this conversion. Notice the terminal
                states are
                in the first rows.
            </p>

            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`\\begin{bmatrix}
                    1 & 0 & 0 & 0 & 0 & 0 \\\\
                    0 & 1 & 0 & 0 & 0 & 0 \\\\
                    0 & 0 & 1 & 0 & 0 & 0 \\\\
                    0 & 0 & 0 & 1 & 0 & 0 \\\\
                    0 & 0 & 0 & 1/2 & 0 & 1/2 \\\\
                    0 & 1/3 & 2/9 & 0 & 4/9 & 0 \\\\
                    \\end{bmatrix}`}
                </MathJax>
            </MathJaxContext>

            <p>
                Now we have I, 0, R, and Q matrices.
            </p>

            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`$$
                        I =
                        \\begin{bmatrix}
                        1 & 0 & 0 & 0 \\\\
                        0 & 1 & 0 & 0 \\\\
                        0 & 0 & 1 & 0 \\\\
                        0 & 0 & 0 & 1 \\\\
                        \\end{bmatrix}
                        ,
                        \\;\\;
                        0 =
                        \\begin{bmatrix}
                        0 & 0 \\\\
                        0 & 0 \\\\
                        0 & 0 \\\\
                        0 & 0 \\\\
                        \\end{bmatrix}
                        $$
                        
                        $$
                        R =
                        \\begin{bmatrix}
                        0 & 0 & 0 & 1/2 \\\\
                        0 & 1/3 & 2/9 & 0 \\\\
                        \\end{bmatrix}
                        ,
                        \\;\\;
                        Q =
                        \\begin{bmatrix}
                        0 & 1/2 \\\\
                        4/9 & 0 \\\\
                        \\end{bmatrix}
                        $$`}
                </MathJax>
            </MathJaxContext>

            <p>
                We can calculate the matrix F.
            </p>

            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`\\begin{align}
                    F &= (I - Q)^{-1} = \\left( \\begin{bmatrix}
                    1 & 0 \\\\
                    0 & 1 \\\\
                    \\end{bmatrix} - \\begin{bmatrix}
                    0 & 1/2 \\\\
                    4/9 & 0 \\\\
                    \\end{bmatrix}
                    \\right)^{-1} \\\\
                    &= \\begin{bmatrix}
                    1 & -1/2 \\\\
                    -4/9 & 1 \\\\
                    \\end{bmatrix}^{-1} = \\begin{bmatrix}
                    9/7 & 9/14 \\\\
                    4/7 & 9/7 \\\\
                    \\end{bmatrix}
                    \\end{align}`}
                </MathJax>
            </MathJaxContext>

            <p>
                Finally, we can calculate the absorbing matrix FR.
            </p>

            <MathJaxContext config={mathJaxConfig}>
                <MathJax>
                    {`\\begin{align}
                    FR = F \\cdot R &= \\begin{bmatrix}
                    9/7 & 9/14 \\\\
                    4/7 & 9/7 \\\\
                    \\end{bmatrix} \\cdot \\begin{bmatrix}
                    0 & 0 & 0 & 1/2 \\\\
                    0 & 1/3 & 2/9 & 0 \\\\
                    \\end{bmatrix} \\\\
                    &= \\begin{bmatrix}
                    0 & 3/14 & 1/7 & 9/14 \\\\
                    0 & 3/7 & 2/7 & 2/7 \\\\
                    \\end{bmatrix} \\\\
                    \\end{align}`}
                </MathJax>
            </MathJaxContext>

            <p>
                Check out the first row of the FR matrix. These are the probabilities we are looking for. Making a
                common
                denominator gives us the answer in the form of [s2.numerator, s3.numerator, s4.numerator,
                s5.numerator,
                denominator], which is [0, 3, 2, 9, 14].
            </p>
            <p>
                You will also notice the <code>greatest_common_divisor</code> and <code>least_common_multiple</code>
                functions in the code, which are used to find the common denominator.
            </p>

            <h2>Code</h2>
            <pre><code className="language-python">{`from fractions import Fraction

def create_transition_matrix(m):
    """
    Converts the given matrix to a transition matrix with probabilities.

    Creates the form
    I | 0
    --|--
    R | Q
    where I is the identity matrix, 0 is the zero matrix, R and Q are the submatrices.

    To achieve the desired form, we need to do the following:
    1. Convert the given numbers to probabilities by dividing each number by the sum of the row.
    2. Determine the terminal states (rows with all zeros).
    3. Convert the 0 in the self directed positions to 1.
    4. Reorder the columns and rows where the terminal states are first.
    """

    # Convert the given numbers to probabilities
    for i in range(len(m)):
        row_sum = sum(m[i])
        for j in range(len(m[i])):
            m[i][j] /= row_sum if row_sum != 0 else 1

    # Determine the terminal states
    terminal_states = []
    non_terminal_states = []
    for i in range(len(m)):
        if sum(m[i]) == 0:
            terminal_states.append(i)
        else:
            non_terminal_states.append(i)

    # Convert the 0 in the self directed positions to 1
    for i in range(len(m)):
        if i in terminal_states:
            m[i][i] = 1

    # Reorder the columns and rows where the terminal states are first
    new_m = []
    for i in terminal_states:
        new_m.append([m[i][j] for j in terminal_states + non_terminal_states])
    for i in non_terminal_states:
        new_m.append([m[i][j] for j in terminal_states + non_terminal_states])

    # Return the transition matrix and the number of terminal states
    return new_m, len(terminal_states)

def inverse_matrix(matrix):
    """
    Returns the inverse of the given matrix using the Gauss-Jordan elimination method.
    """
    size = len(matrix)
    inverse = [[0 if row != column else 1 for column in range(size)] for row in range(size)]

    for i in range(size):
        if matrix[i][i] == 0:
            for j in range(i + 1, size):
                if matrix[j][i] != 0:
                    matrix[i], matrix[j] = matrix[j], matrix[i]
                    inverse[i], inverse[j] = inverse[j], inverse[i]
                    break
            else:
                raise ValueError("Matrix is not invertible")
        for j in range(i + 1, size):
            ratio = matrix[j][i] / matrix[i][i]
            for k in range(size):
                matrix[j][k] -= ratio * matrix[i][k]
                inverse[j][k] -= ratio * inverse[i][k]
    for i in range(size - 1, -1, -1):
        for j in range(i - 1, -1, -1):
            ratio = matrix[j][i] / matrix[i][i]
            for k in range(size):
                matrix[j][k] -= ratio * matrix[i][k]
                inverse[j][k] -= ratio * inverse[i][k]
        div = matrix[i][i]
        for k in range(size):
            matrix[i][k] /= div
            inverse[i][k] /= div
    return inverse

def calculate_fr(transition_matrix, terminal_states):
    """
    Calculates the fundamental matrix and the absorbing matrix from the given transition matrix.
    """
    # Split the transition matrix into the fundamental matrix and the absorbing matrix
    Q = []
    R = []

    # The matrix is in the form of
    # I | 0
    # --|--
    # R | Q
    # so R is on the bottom left and Q is on the bottom right

    # Q is the submatrix of transition_matrix containing transitions between non-terminal states
    # R is the submatrix of transition_matrix containing transitions from non-terminal states to terminal states
    for i in range(terminal_states, len(transition_matrix)):
        Q.append(transition_matrix[i][terminal_states:])
        R.append(transition_matrix[i][:terminal_states])

    # Calculate the fundamental matrix
    I = []
    for i in range(len(Q)):
        I.append([0] * len(Q))
        I[i][i] = 1

    # F = (I - Q)^-1
    F = []
    for i in range(len(Q)):
        F.append([I[i][j] - Q[i][j] for j in range(len(Q))])

    F = inverse_matrix(F)

    # Calculate the absorbing matrix which is F*R
    FR = []
    for i in range(len(F)):
        FR.append([0] * len(R[0]))
        for j in range(len(R[0])):
            for k in range(len(R)):
                FR[i][j] += F[i][k] * R[k][j]

    return FR

def greates_common_divisor(list_of_numbers):
    """
    Returns the greatest common divisor of the given numbers.
    """
    if len(list_of_numbers) == 2:
        a, b = list_of_numbers
        while b:
            a, b = b, a % b
        return a
    else:
        return greates_common_divisor([list_of_numbers[0], greates_common_divisor(list_of_numbers[1:])])

def least_common_multiple(list_of_numbers):
    """
    Returns the least common multiple of the given numbers.
    """
    lcm = list_of_numbers[0]
    for i in list_of_numbers[1:]:
        lcm = lcm * i // greates_common_divisor([lcm, i])
    return lcm

def solution(m):
    if len(m) == 1:
        return [1, 1]

    # Convert the given matrix to a transition matrix with probabilities
    transition_matrix, terminal_states_count = create_transition_matrix(m)

    # Calculate the fundamental matrix and the absorbing matrix from the given transition matrix
    FR = calculate_fr(transition_matrix, terminal_states_count)

    # Converting the probabilities to fractions
    FR = [[Fraction(x).limit_denominator() for x in FR[i]] for i in range(len(FR))]

    # Since the starting state is 0, we can directly return the first row of the absorbing matrix
    probabilities = FR[0]

    # Now we need to find the least common multiple of the denominators
    denominators = [x.denominator for x in probabilities]
    lcm = least_common_multiple(denominators)

    # Return the result
    result = [x.numerator * (lcm // x.denominator) for x in probabilities] + [lcm]

    return result`}</code></pre>
        </div>
    );
}